import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { SideBarMenuItem } from '../sidebar/types';

export const SETUP_LABEL = 'Setup / Configuration';

// The server names this section differently per facility
// ("Setup / Configuration", "Configuration", "Setup").
export const isSetupSection = (label?: string | null) =>
  /^\s*(setup|configuration)\b|\bconfiguration\s*$/i.test(label ?? '');

export const isLinkActive = (link: string | null | undefined, path: string) =>
  !!link && (link === path || (link !== '/' && path.startsWith(link)));

export type NavSection = {
  item: SideBarMenuItem;
  children: SideBarMenuItem[];
  // First page of the section; clicking the section label opens it.
  href: string;
  active: boolean;
};

export const useNavModel = (items: SideBarMenuItem[]) => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const sections: NavSection[] = items.map((item) => {
      const children = item.children ?? [];
      const href = children[0]?.link ?? item.link ?? '/';
      const active = children.length
        ? children.some((child) => isLinkActive(child.link, pathname))
        : isLinkActive(item.link, pathname);
      return { item, children, href, active };
    });

    const mainSections = sections.filter((s) => !isSetupSection(s.item.label));
    const setupSection = sections.find((s) => isSetupSection(s.item.label));
    const currentSection = sections.find((s) => s.active);
    const currentPage = currentSection?.children.find((child) =>
      isLinkActive(child.link, pathname)
    );

    return {
      pathname,
      sections,
      mainSections,
      setupSection,
      currentSection,
      currentPage,
    };
  }, [items, pathname]);
};
